import { encodeBase64Url, toArrayBuffer } from './encoding.js';
const DefaultCredentialOperationTimeoutMs = 15_000;
function requirePublicKeyCredential() {
    if (typeof PublicKeyCredential === 'undefined') {
        throw new Error('WebAuthn is not available in this environment.');
    }
    return PublicKeyCredential;
}
function requireCredentialsContainer() {
    if (typeof navigator === 'undefined' || navigator.credentials === undefined) {
        throw new Error('Credential management is not available in this environment.');
    }
    return navigator.credentials;
}
function createCredentialOperationSignal() {
    if (typeof AbortController === 'undefined' || typeof setTimeout === 'undefined') {
        return {
            clear() {
                return;
            }
        };
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort(new DOMException('The credential operation timed out before the browser completed user verification.', 'AbortError'));
    }, DefaultCredentialOperationTimeoutMs);
    return {
        clear() {
            clearTimeout(timeoutId);
        },
        signal: controller.signal
    };
}
async function runCredentialOperation(operation) {
    const { clear, signal } = createCredentialOperationSignal();
    const timeoutError = new Error('The browser did not complete local user verification before the WebAuthn request timed out.');
    const timeoutPromise = new Promise((_resolve, reject) => {
        if (signal === undefined) {
            return;
        }
        signal.addEventListener('abort', () => {
            reject(timeoutError);
        }, { once: true });
    });
    try {
        return await Promise.race([operation(signal), timeoutPromise]);
    }
    catch (error) {
        if (error === timeoutError) {
            throw error;
        }
        if (error instanceof DOMException && error.name === 'AbortError') {
            throw timeoutError;
        }
        throw error;
    }
    finally {
        clear();
    }
}
function getAuthenticatorAttachment(credential) {
    return credential.authenticatorAttachment ?? undefined;
}
function toPrfResults(result) {
    const values = result?.results;
    if (values === undefined) {
        return undefined;
    }
    const first = encodeBase64Url(toArrayBuffer(values.first));
    const second = values.second === undefined ? undefined : encodeBase64Url(toArrayBuffer(values.second));
    return {
        first,
        second
    };
}
function toClientExtensionResults(result) {
    return {
        appid: result.appid,
        credProps: result.credProps,
        hmacCreateSecret: result.hmacCreateSecret,
        largeBlob: result.largeBlob === undefined
            ? undefined
            : {
                supported: result.largeBlob.supported,
                written: result.largeBlob.written
            },
        prf: result.prf === undefined
            ? undefined
            : {
                enabled: result.prf.enabled,
                results: toPrfResults(result.prf)
            }
    };
}
function toAttestationResponse(response) {
    return {
        attestationObject: encodeBase64Url(response.attestationObject),
        clientDataJson: encodeBase64Url(response.clientDataJSON),
        transports: response.getTransports()
    };
}
function toAssertionResponse(response) {
    return {
        authenticatorData: encodeBase64Url(response.authenticatorData),
        clientDataJson: encodeBase64Url(response.clientDataJSON),
        signature: encodeBase64Url(response.signature),
        userHandle: response.userHandle === null ? undefined : encodeBase64Url(response.userHandle)
    };
}
function isPublicKeyCredential(credential) {
    return typeof PublicKeyCredential !== 'undefined' && credential instanceof PublicKeyCredential;
}
async function getPublicKeyAssertion(options) {
    const credential = await runCredentialOperation(async (signal) => await requireCredentialsContainer().get({
        publicKey: options,
        signal
    }));
    if (!isPublicKeyCredential(credential)) {
        throw new Error('Credential request did not return a public key credential.');
    }
    if (!(credential.response instanceof AuthenticatorAssertionResponse)) {
        throw new Error('Credential request did not return an assertion response.');
    }
    return {
        authenticatorAttachment: getAuthenticatorAttachment(credential),
        clientExtensionResults: toClientExtensionResults(credential.getClientExtensionResults()),
        id: credential.id,
        rawId: encodeBase64Url(credential.rawId),
        response: toAssertionResponse(credential.response),
        type: credential.type
    };
}
export function parsePasskeyRegistrationRequest(request) {
    return requirePublicKeyCredential().parseCreationOptionsFromJSON(request);
}
export function parsePasskeyAuthenticationRequest(request) {
    return requirePublicKeyCredential().parseRequestOptionsFromJSON(request);
}
export async function createPasskeyRegistration(request) {
    const options = parsePasskeyRegistrationRequest(request);
    const credential = await runCredentialOperation(async (signal) => await requireCredentialsContainer().create({
        publicKey: options,
        signal
    }));
    if (!isPublicKeyCredential(credential)) {
        throw new Error('Credential creation did not return a public key credential.');
    }
    if (!(credential.response instanceof AuthenticatorAttestationResponse)) {
        throw new Error('Credential creation did not return an attestation response.');
    }
    return {
        authenticatorAttachment: getAuthenticatorAttachment(credential),
        clientExtensionResults: toClientExtensionResults(credential.getClientExtensionResults()),
        id: credential.id,
        rawId: encodeBase64Url(credential.rawId),
        response: toAttestationResponse(credential.response),
        type: credential.type
    };
}
export async function getPasskeyAuthentication(request) {
    const options = parsePasskeyAuthenticationRequest(request);
    return await getPublicKeyAssertion(options);
}
export async function getPasskeyAuthenticationFromOptions(options) {
    return await getPublicKeyAssertion(options);
}
